// @flow
import type { Router as RouterType } from 'next/router'

import { merge } from 'timm'
import {
  ADMIN,
  DRIVER,
  DISPATCHER,
  PROVIDER,
  REVIEWER,
  LOGGED_OUT,
  LOGGED_IN
} from './withAuthentication/roles'
import type { Link as LinkType, ListLinkParams } from '../types/Link'
import type {
  CanDoAction,
  PagePermissionsWithActions,
  Role
} from '../types/Role'
import type { Page } from '../types/Routes'

const linkPrefix = process.env.LINK_PREFIX || ''

function withPrefix (path: string, query?: Object = {}): LinkType {
  return {
    href: {
      pathname: `/${path}`,
      query,
      origin: global && global.location && global.location.origin
    },
    as: {
      pathname: `${linkPrefix}/${path ? path + '/' : path}`,
      query
    }
  }
}

export function doesRouteMatchWith (
  router: RouterType,
  matchingRoutes: LinkType | Array<LinkType>
) {
  return Boolean(
    [].concat(matchingRoutes).some(matcher => {
      return matcher.href.pathname === router.pathname
    })
  )
}

function canAccess (
  userRoles: ?Array<Role>,
  pagePermissions: PagePermissionsWithActions,
  action: CanDoAction = 'access'
): LinkType => LinkType {
  let canAccess = false
  if (userRoles) {
    canAccess = Boolean(
      pagePermissions[action].find(pagePermission =>
        (userRoles || []).includes(pagePermission)
      )
    )
  }
  return link => merge(link, canAccess ? { canAccess } : {})
}

function canAccessWithPrefix ({
  userRoles,
  page,
  qs
}: {
  userRoles?: Array<Role>,
  page: Page,
  qs?: Object
}): LinkType {
  return canAccess(userRoles, pagePermissions[page])(withPrefix(page, qs))
}

const pagePermissions: {
  [id: Page]: PagePermissionsWithActions
} = {}

export const LOGIN: Page = 'login'
pagePermissions[LOGIN] = {
  access: [LOGGED_OUT]
}
export function login ({
  pathname = '/',
  search = ''
}: {
  pathname?: string,
  search?: string
}): LinkType {
  return withPrefix(LOGIN, {
    redirect_to: `${pathname}${search}`
  })
}

export const HOME: Page = ''
pagePermissions[HOME] = {
  access: [LOGGED_OUT]
}
export function home (): LinkType {
  return withPrefix(HOME)
}

export const NOT_AUTHORIZED: Page = '403'
pagePermissions[NOT_AUTHORIZED] = {
  access: [LOGGED_IN]
}
export function notAuthorized (): LinkType {
  return withPrefix(NOT_AUTHORIZED)
}

export const CHAUFFEURS_LIST: Page = 'chauffeurs'
pagePermissions[CHAUFFEURS_LIST] = {
  access: [ADMIN, DISPATCHER, REVIEWER],
  add: [ADMIN]
}
export function chauffeursList (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: CHAUFFEURS_LIST })
}

export const CHAUFFEUR_ADD: Page = 'chauffeur-add'
pagePermissions[CHAUFFEUR_ADD] = {
  access: [ADMIN]
}

export function chauffeurAdd (): LinkType {
  return withPrefix(CHAUFFEUR_ADD)
}

export const CHAUFFEUR_DETAIL: Page = 'chauffeur-detail'
pagePermissions[CHAUFFEUR_DETAIL] = {
  access: [ADMIN, DISPATCHER, REVIEWER],
  deleteResource: [ADMIN],
  edit: [ADMIN]
}
export function chauffeurDetail (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: CHAUFFEUR_DETAIL })
}

export const CHAUFFEUR_DOCS: Page = 'chauffeur-docs'
pagePermissions[CHAUFFEUR_DOCS] = {
  access: [ADMIN]
}
export function chauffeurDocs (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: CHAUFFEUR_DOCS })
}

export const CHAUFFEUR_DOCUMENT_UPLOAD: Page = 'chauffeur-doc-upload'
pagePermissions[CHAUFFEUR_DOCUMENT_UPLOAD] = {
  access: [ADMIN]
}
export function chauffeurDocumentUpload (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: CHAUFFEUR_DOCUMENT_UPLOAD })
}

export const CHAUFFEUR_EDIT: Page = 'chauffeur-edit'
pagePermissions[CHAUFFEUR_EDIT] = {
  access: [ADMIN]
}

export function chauffeurEdit ({ qs: { id = '' } = {} }: Object): LinkType {
  return withPrefix(CHAUFFEUR_EDIT, { id })
}

export const LSP_DOCS: Page = 'lsp-docs'
pagePermissions[LSP_DOCS] = {
  access: [ADMIN]
}

export function lspDocs (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: LSP_DOCS })
}

export const LSP_DOCUMENT_UPLOAD: Page = 'lsp-doc-upload'
pagePermissions[LSP_DOCUMENT_UPLOAD] = {
  access: [ADMIN]
}
export function lspDocumentUpload (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: LSP_DOCUMENT_UPLOAD })
}

export const VEHICLES_LIST: Page = 'vehicles'
pagePermissions[VEHICLES_LIST] = {
  access: [ADMIN, DISPATCHER],
  add: [ADMIN]
}
export function vehiclesList (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: VEHICLES_LIST })
}

export const VEHICLE_DETAIL: Page = 'vehicle-detail'
pagePermissions[VEHICLE_DETAIL] = {
  access: [ADMIN, DISPATCHER]
}
export function vehicleDetail (detailParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...detailParams, page: VEHICLE_DETAIL })
}

export const VEHICLE_ADD: Page = 'vehicle-add'
pagePermissions[VEHICLE_ADD] = {
  access: [ADMIN]
}
export function vehicleAdd (): LinkType {
  return withPrefix(VEHICLE_ADD)
}

export const VEHICLE_DOCS: Page = 'vehicle-docs'
pagePermissions[VEHICLE_DOCS] = {
  access: [ADMIN]
}
export function vehicleDocs (detailParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...detailParams, page: VEHICLE_DOCS })
}

export const VEHICLE_DOCUMENT_UPLOAD: Page = 'vehicle-doc-upload'
pagePermissions[VEHICLE_DOCUMENT_UPLOAD] = {
  access: [ADMIN]
}
export function vehicleDocumentUpload (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: VEHICLE_DOCUMENT_UPLOAD })
}

export const RATINGS_LIST: Page = 'ratings'
pagePermissions[RATINGS_LIST] = {
  access: [ADMIN]
}

export function ratingsList (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: RATINGS_LIST })
}

export const OFFER_LIST: Page = 'offers'
pagePermissions[OFFER_LIST] = {
  access: [PROVIDER]
}

export function offers (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: OFFER_LIST })
}

export const OFFER_DETAIL: Page = 'offer-detail'
pagePermissions[OFFER_DETAIL] = {
  access: [PROVIDER],
  acceptOffer: [PROVIDER]
}
export function offerDetail ({ qs: { id = '' } = {} }: Object): LinkType {
  return withPrefix(OFFER_DETAIL, { id })
}

export const PLANNED_LIST: Page = 'planned'
pagePermissions[PLANNED_LIST] = {
  access: [DISPATCHER, DRIVER],
  assignChauffeur: [DISPATCHER],
  assignVehicle: [DISPATCHER, DRIVER]
}
export function planned (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: PLANNED_LIST })
}

export const PLANNED_DETAIL: Page = 'planned-detail'
pagePermissions[PLANNED_DETAIL] = {
  access: [DISPATCHER, DRIVER],
  assignChauffeur: [DISPATCHER],
  seePrice: [ADMIN],
  assignVehicle: [DISPATCHER, DRIVER]
}
export function plannedDetail ({ qs: { id = '' } = {} }: Object): LinkType {
  return withPrefix(PLANNED_DETAIL, { id })
}

export const FINISHED_LIST: Page = 'finished'
pagePermissions[FINISHED_LIST] = {
  access: [DISPATCHER, DRIVER, REVIEWER],
  review: [REVIEWER]
}
export function finished (listParams: ListLinkParams): LinkType {
  return canAccessWithPrefix({ ...listParams, page: FINISHED_LIST })
}

export const FINISHED_DETAIL: Page = 'finished-detail'
pagePermissions[FINISHED_DETAIL] = {
  access: [DISPATCHER, DRIVER, REVIEWER],
  review: [REVIEWER],
  seePrice: [ADMIN]
}
export function finishedDetail ({ qs: { id = '' } = {} }: Object): LinkType {
  return withPrefix(FINISHED_DETAIL, { id })
}

export const PENDING_OFFERS: Page = 'pending-offers'
pagePermissions[PENDING_OFFERS] = {
  access: [ADMIN, DISPATCHER]
}

export function pendingOffers ({ qs: { id = '' } = {} }: Object): LinkType {
  return withPrefix(PENDING_OFFERS, { id })
}
export const HELP: Page = 'help'
pagePermissions[HELP] = {
  access: [LOGGED_IN]
}
export function help (): LinkType {
  // $FlowIgnore external link
  return {
    href: 'https://partner-help.blacklane.com',
    as: 'https://partner-help.blacklane.com'
  }
}
export const PROFILE: Page = 'me'
pagePermissions[PROFILE] = {
  access: [LOGGED_IN]
}
export function me (): LinkType {
  return withPrefix(PROFILE)
}

export const RESET_PASSWORD: Page = 'reset-password'
pagePermissions[RESET_PASSWORD] = {
  access: [LOGGED_OUT]
}
export function resetPassword (): LinkType {
  return withPrefix(RESET_PASSWORD)
}

export const UPDATE_PASSWORD: Page = 'update-password'
pagePermissions[UPDATE_PASSWORD] = {
  access: [LOGGED_OUT]
}
export function updatePassword (): LinkType {
  return withPrefix(UPDATE_PASSWORD)
}

export const PUBLIC: Page = '__publicPage__'
pagePermissions[PUBLIC] = {
  access: []
}

export const permissions = pagePermissions
